import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  verticallyCenteredLabelSx,
  fieldHelpSx,
} from '../../../theme/fieldTokens';
import { formatCash } from '../../../utils/formatCash';

export const LIMIT_MODE_DEFAULT = 'default';
export const LIMIT_MODE_CUSTOM = 'custom';

// Same muted uppercase as the table headers
export const groupLabelSx = {
  color: '#8fa3d4',
  fontSize: '14px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  textAlign: 'left',
  mb: 1,
};

// Second line inside a pick: the amounts or the hall name
export const pickSubStyle = {
  display: 'block',
  fontSize: '15px',
  fontWeight: 500,
  marginTop: '2px',
};

export const pickClass = (on) => `btn-pick${on ? ' is-on' : ''}`;

export const fieldSx = {
  width: '100%',
  '& .MuiInputBase-input': {
    fontSize: '22px',
    fontVariantNumeric: 'tabular-nums',
  },
  // The dark skin's fieldset rule outranks MUI's own error border
  '& .MuiOutlinedInput-root.Mui-error fieldset': {
    borderColor: '#e8895f',
  },
};

// First broken rule as { key, amount, field }, or null; the backend enforces the same rules
export const limitsError = (value, defaults) => {
  if (value.mode !== LIMIT_MODE_CUSTOM) return null;
  if (!defaults) return { key: 'limits.loadFailed', field: 'both' };

  const daily = Number(value.limitDaily);
  const monthly = Number(value.limitMonthly);

  if (!daily && !monthly) return { key: 'limits.errorEmpty', field: 'both' };
  if (!daily) return { key: 'limits.errorEmpty', field: 'limitDaily' };
  if (!monthly) return { key: 'limits.errorEmpty', field: 'limitMonthly' };
  if (daily > defaults.limitDaily) {
    return {
      key: 'limits.errorDailyOver',
      amount: defaults.limitDaily,
      field: 'limitDaily',
    };
  }
  if (monthly > defaults.limitMonthly) {
    return {
      key: 'limits.errorMonthlyOver',
      amount: defaults.limitMonthly,
      field: 'limitMonthly',
    };
  }
  if (daily > monthly) {
    return { key: 'limits.errorDailyOverMonthly', field: 'limitDaily' };
  }
  return null;
};

export const limitsErrorText = (t, error) =>
  t(error.key, { amount: formatCash(error.amount) });

// value = { mode, limitDaily, limitMonthly }
function LimitsPicker({ value, onChange, defaults }) {
  const { t } = useTranslation();

  const isCustom = value.mode === LIMIT_MODE_CUSTOM;
  const error = limitsError(value, defaults);

  const set = (patch) => onChange({ ...value, ...patch });

  // Switching to custom starts from the standard, so lowering one limit is one edit
  const chooseCustom = () =>
    set({
      mode: LIMIT_MODE_CUSTOM,
      limitDaily: value.limitDaily || String(defaults.limitDaily),
      limitMonthly: value.limitMonthly || String(defaults.limitMonthly),
    });

  const handleDigits = (field) => (e) =>
    set({ [field]: e.target.value.replace(/\D/g, '').replace(/^0+/, '') });

  const hasError = (field) =>
    !!error && (error.field === field || error.field === 'both');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography sx={groupLabelSx}>{t('limits.modeLabel')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <button
            type="button"
            className={pickClass(!isCustom)}
            onClick={() => set({ mode: LIMIT_MODE_DEFAULT })}
          >
            {t('limits.standard')}
            {defaults && (
              <span style={pickSubStyle}>
                {formatCash(defaults.limitDaily)} /{' '}
                {formatCash(defaults.limitMonthly)} kr
              </span>
            )}
          </button>
          <button
            type="button"
            className={pickClass(isCustom)}
            onClick={chooseCustom}
            disabled={!defaults}
          >
            {t('limits.modeCustom')}
          </button>
        </Box>
      </Box>

      {isCustom && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          {[
            ['limitDaily', 'limits.daily', defaults.limitDaily],
            ['limitMonthly', 'limits.monthly', defaults.limitMonthly],
          ].map(([field, labelKey, max]) => (
            <Box key={field} sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                autoComplete="off"
                label={t(labelKey)}
                value={value[field]}
                onChange={handleDigits(field)}
                error={hasError(field)}
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                InputLabelProps={{ sx: verticallyCenteredLabelSx }}
                sx={fieldSx}
              />
              <Typography sx={{ ...fieldHelpSx, textAlign: 'left', mb: 0 }}>
                {t('limits.max', { amount: formatCash(max) })}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {error && (
        <Typography
          sx={{
            color: '#e8895f',
            fontSize: '16px',
            fontWeight: 700,
            textAlign: 'left',
            mt: -1,
          }}
        >
          {limitsErrorText(t, error)}
        </Typography>
      )}
    </Box>
  );
}

export default LimitsPicker;
